import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import Seo from "../components/Seo";
import StructuredData from "../components/StructuredData";
import { useEnquiry } from "../context/EnquiryContext";
import { KITCHEN, PHOTOS } from "../lib/assets";

const NEEDS = [
  "Available space", "Family requirements", "Storage needs", "Cooking habits",
  "Aesthetics", "Functionality", "Budget",
];

const FINISHES = [
  {
    name: "Acrylic",
    img: KITCHEN.acrylic,
    alt: "Acrylic modular kitchen with a sleek, high-gloss handleless finish",
    copy: "A contemporary, high-sheen finish for homeowners who want a clean, seamless and distinctly modern kitchen. Acrylic kitchens reflect light beautifully and keep the look crisp and uncluttered.",
  },
  {
    name: "PU",
    img: KITCHEN.pu,
    alt: "English-style PU painted modular kitchen in soft sage green with shaker doors",
    copy: "A refined, painted finish that delivers a smooth, sophisticated surface. PU kitchens offer flexibility in colour and sheen, making them a versatile choice for a tailored, English-inspired look.",
  },
  {
    name: "Laminate",
    img: KITCHEN.laminate,
    alt: "L-shaped modular kitchen in a warm Irish-cream laminate finish",
    copy: "A practical and versatile option that balances appearance, durability and value. Laminate kitchens come in a wide range of shades and textures, from warm creams to natural wood grains.",
  },
];

const MATERIALS = [
  { name: "MDF", img: KITCHEN.coreMdf, alt: "MDF engineered board sample showing a smooth, uniform dense fibre core", copy: "An engineered board with a smooth, consistent surface — well suited to applications that need an even base for finishes." },
  { name: "HDHMR", img: KITCHEN.coreHdhmr, alt: "HDHMR high-density moisture-resistant board sample edge", copy: "A high-density engineered board with improved moisture resistance, suited to applications where added resistance to moisture is desirable." },
  { name: "BWR Plywood", img: KITCHEN.coreBwr, alt: "BWR plywood sample showing layered veneer plies at the cut edge", copy: "Boiling Water Resistant plywood, suited to areas where a greater degree of moisture resistance is required." },
];

const APPROACH = [
  "Efficient work zones", "Usable, well-planned storage", "Drawers and shutters",
  "Smart corner utilisation", "Considered appliance placement", "A comfortable countertop workflow",
  "Easy access to everyday items", "Balanced, calm visual proportions",
];

const LAYOUTS = [
  { name: "L-Shaped", img: KITCHEN.lShaped, note: "Two connected runs — efficient for corners." },
  { name: "Straight", img: KITCHEN.straight, note: "A single run for compact spaces." },
  { name: "Parallel / Galley", img: KITCHEN.parallel, note: "Two facing runs for busy cooking." },
  { name: "U-Shaped", img: KITCHEN.uShaped, note: "Three runs with generous storage." },
  { name: "Island", img: KITCHEN.island, note: "A central block, where space permits." },
];

const PROCESS = [
  "Understand your requirements", "Measure and plan the space", "Develop the kitchen design",
  "Finalise materials and finishes", "Prepare the modular components", "Install at site",
  "Quality checks", "Deliver the completed kitchen",
];

const COMMITMENTS = [
  { t: "10-Year Warranty on Modulars", d: "Peace of mind on the modular units we build and install for you." },
  { t: "Quality Checks", d: "Every kitchen is inspected against a snag list before handover." },
  { t: "Transparent Costing", d: "Clear, itemised pricing — so you always know what you're paying for." },
  { t: "Timely Delivery", d: "Planned schedules and weekly progress, delivered on the dates we commit." },
];

export default function ModularKitchens() {
  const { openEnquiry } = useEnquiry();

  const scrollToDesigns = () => {
    const el = document.getElementById("mk-finishes");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="bg-warm" data-testid="modular-kitchens-page">
      <Seo
        title="Modular Kitchens in Noida & Delhi NCR | DIDIT"
        description="Explore modular kitchens by DIDIT, designed for modern Indian homes in Noida and Delhi NCR. Choose from acrylic, PU and laminate finishes with MDF, HDHMR and BWR plywood core materials."
        canonical="https://didithomes.com/modular-kitchens"
      />
      <StructuredData
        id="modular-kitchens"
        data={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Modular Kitchens",
            serviceType: "Modular Kitchen Design & Installation",
            description:
              "Custom modular kitchens for modern Indian homes in Noida and Delhi NCR — acrylic, PU and laminate finishes on MDF, HDHMR and BWR plywood cores.",
            areaServed: ["Noida", "Greater Noida", "Delhi NCR"],
            provider: { "@type": "HomeAndConstructionBusiness", name: "DIDIT", url: "https://didithomes.com/" },
            url: "https://didithomes.com/modular-kitchens",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://didithomes.com/" },
              { "@type": "ListItem", position: 2, name: "Modular Kitchens", item: "https://didithomes.com/modular-kitchens" },
            ],
          },
        ]}
      />

      {/* 1 — HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <Reveal><Overline>Modular Kitchens</Overline></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-navy mt-6 leading-[1.02]">
                Modular Kitchens Designed Around Your Home
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/75 text-base md:text-lg leading-relaxed mt-6 max-w-xl">
                Thoughtfully designed modular kitchens that balance everyday functionality, smart storage and a style that feels right for your home.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-3 mt-9">
                <button onClick={() => openEnquiry("consultation", "modular-kitchens-hero")} data-testid="mk-hero-consultation-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-navy text-warm px-8 py-4 text-sm font-medium hover:bg-terracotta transition-colors">
                  Get Free Consultation
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={scrollToDesigns} data-testid="mk-hero-explore-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-navy text-navy px-8 py-4 text-sm font-medium hover:bg-navy hover:text-warm transition-colors">
                  Explore Our Designs
                </button>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] md:aspect-[5/6] overflow-hidden border border-navy/10">
              <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-terracotta z-10" />
              <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-terracotta z-10" />
              <img src={KITCHEN.hero} alt="DIDIT modular kitchen in a denim-suede finish with a white quartz countertop and warm natural daylight"
                className="w-full h-full object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2 — INTRODUCTION */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden border border-navy/10">
              <img src={PHOTOS.kitchen1} alt="A DIDIT modular kitchen planned around the way a family lives"
                className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <Reveal><Overline>The DIDIT approach</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight">
                A Kitchen Designed Around the Way You Live
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/75 leading-relaxed mt-6">
                No two homes cook, store or gather the same way. Every DIDIT modular kitchen design starts with your space and your routine — then we shape the layout, storage and finishes around it. It's a considered, custom modular kitchen rather than a template.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-2.5 mt-7">
                {NEEDS.map((n) => (
                  <span key={n} className="text-sm text-navy/80 border border-navy/15 rounded-full px-4 py-1.5 bg-white">{n}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-ink/70 leading-relaxed mt-7">
                And because DIDIT handles <Link to="/" className="text-terracotta hover:underline">home interiors</Link> end to end, your kitchen is designed, installed and delivered by one accountable team — the same <Link to="/how-it-works" className="text-terracotta hover:underline">Design. Install. Deliver.</Link> journey we follow for every home.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3 — FINISHES */}
      <section id="mk-finishes" className="py-20 md:py-28 bg-white border-y border-navy/10" data-testid="mk-finishes">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline>Finishes</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight max-w-2xl">
              Finishes That Set the Tone
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-ink/70 leading-relaxed mt-5 max-w-xl">
              The finish shapes how your kitchen looks and feels day to day. We work with three finish families — each with its own character.
            </p>
          </Reveal>
          <div className="mt-14 space-y-16 md:space-y-24">
            {FINISHES.map((f, i) => (
              <div key={f.name}
                className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}
                data-testid={`mk-finish-${f.name.toLowerCase()}`}>
                <Reveal className="md:[direction:ltr]">
                  <div className="group relative overflow-hidden border border-navy/10 aspect-[4/3]">
                    <img src={f.img} alt={f.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                </Reveal>
                <Reveal delay={0.05} className="md:[direction:ltr]">
                  <span className="font-display text-terracotta text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl md:text-4xl font-light text-navy mt-3 leading-tight">{f.name} Kitchens</h3>
                  <p className="text-ink/75 leading-relaxed mt-5 max-w-md">{f.copy}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — CORE MATERIALS */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Core materials</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight">
                Built on the Right Core Materials
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/75 leading-relaxed mt-6">
                The finish you see and the core material underneath are two separate choices. Picking the right core for each part of the kitchen is what makes it work well over time.
              </p>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mt-12">
            {MATERIALS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div className="h-full border border-navy/10 bg-white overflow-hidden" data-testid={`mk-material-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="aspect-[4/3] overflow-hidden border-b border-navy/10 bg-warm">
                    <img src={m.img} alt={m.alt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-8">
                    <span className="font-display text-terracotta text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-display text-2xl text-navy font-normal mt-3">{m.name}</h3>
                    <p className="text-ink/70 text-sm mt-3 leading-relaxed">{m.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — DESIGN APPROACH */}
      <section className="py-20 md:py-28 bg-navy text-warm">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <Reveal><span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-terracotta">Planning</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight mt-5 leading-tight">
                Designed for Real Indian Homes
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-warm/70 leading-relaxed mt-6 max-w-lg">
                Modern Indian kitchens have to do a lot — daily cooking, storage for provisions, appliances big and small. We plan for how kitchens are actually used, not just how they photograph.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {APPROACH.map((a) => (
                <div key={a} className="flex items-start gap-3">
                  <Check size={18} className="text-terracotta mt-0.5 shrink-0" />
                  <span className="text-warm/85 leading-relaxed">{a}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 — KITCHEN TYPES */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Layouts</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight">
                Find the Layout That Fits Your Space
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/75 leading-relaxed mt-6">
                Not every layout suits every home. The right configuration depends on your room's dimensions, its plumbing and electrical services, and how you like to work. Here are the layouts we plan most often.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mt-12">
            {LAYOUTS.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.06}>
                <div className="group relative overflow-hidden aspect-[3/4] border border-navy/10" data-testid={`mk-layout-${l.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <img src={l.img} alt={`${l.name} modular kitchen layout`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-4">
                    <h3 className="font-display text-lg text-warm font-normal leading-tight">{l.name}</h3>
                    <p className="text-warm/70 text-xs mt-1 leading-snug">{l.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — PROCESS */}
      <section className="py-20 md:py-28 bg-white border-y border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Design. Install. Deliver.</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight">
                From Design to Installation
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/75 leading-relaxed mt-6">
                One team takes your kitchen from first idea to a finished space — the same end-to-end process behind every DIDIT project.
              </p>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-12">
            {PROCESS.map((p, i) => (
              <Reveal key={p} delay={(i % 4) * 0.06}>
                <div className="h-full border border-navy/10 bg-warm p-6" data-testid={`mk-step-${i + 1}`}>
                  <span className="font-display text-terracotta text-lg tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-navy mt-3 leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8 — QUALITY / WARRANTY */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Our commitments</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-navy mt-5 leading-tight">
                Built to Last, Backed in Writing
              </h2>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-12">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.07}>
                <div className="h-full border border-navy/10 bg-white p-7" data-testid={`mk-commitment-${i + 1}`}>
                  <h3 className="font-display text-lg text-navy font-normal leading-snug">{c.t}</h3>
                  <p className="text-ink/70 text-sm mt-3 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 12 — CTA */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="bg-navy text-warm p-10 md:p-16">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
                  Let's Design Your Kitchen
                </h2>
                <p className="text-warm/70 leading-relaxed mt-5">
                  Tell us about your space, requirements and ideas. We'll help you turn them into a kitchen designed around the way you live.
                </p>
              </div>
              <button onClick={() => openEnquiry("consultation", "modular-kitchens-cta")} data-testid="mk-cta-consultation-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-warm px-8 py-4 text-sm font-medium hover:bg-warm hover:text-navy transition-colors shrink-0">
                Get Free Consultation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* 13 — internal links */}
            <div className="mt-12 pt-8 border-t border-warm/15 flex flex-wrap gap-x-8 gap-y-3 text-sm">
              <Link to="/" data-testid="mk-link-home" className="inline-flex items-center gap-1.5 text-warm/80 hover:text-terracotta transition-colors">Home <ArrowUpRight size={14} /></Link>
              <Link to="/how-it-works" data-testid="mk-link-how" className="inline-flex items-center gap-1.5 text-warm/80 hover:text-terracotta transition-colors">How It Works <ArrowUpRight size={14} /></Link>
              <button onClick={() => openEnquiry("quote", "modular-kitchens-links")} data-testid="mk-link-quote" className="inline-flex items-center gap-1.5 text-warm/80 hover:text-terracotta transition-colors">Get a Quote <ArrowUpRight size={14} /></button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
